import { FormValues } from '@ahmdigital/form-validation/lib/validators/types';
import { Hidden, P, Span, theme } from '@ahmdigital/ui';
import { KeyboardEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { map } from 'lodash/fp';
import { useFormikContext } from 'formik';
import Icon from '@ahmdigital/icons-web';

import {
  BackButton,
  CloseButton,
  HeadingWrapper,
  LabelSpacer,
  MenuWrapper,
  MobileButton,
  MobileScrollWrapper,
  MobileToggle,
  MobileToggleWrapper,
  OfferTongueWrapper,
  StyledH2,
  StyledP,
  TitleWrapper,
  WidgetWrapper,
} from '../../styles';
import { GetData, StepInputAppearance, SubmitButtonConfig, WidgetButtonSpacing } from '../../types';
import { SectionOfferStrip } from '../../../../pages/insurance/types';
import browser from '../../../../utils/browser';
import device from '../../../../utils/device';
import handleBackClick from '../../utils/handle-click-back';
import OfferTongue from '../../../../pages/travel-insurance/components/header/components/offer-tongue';
import Overlay from '../overlay';
import uncapFpIterator from '../../../../../utils/uncap-fp-iterator';
import useScrollToRefIfOutOfView from '../../hooks/use-scroll-to-ref-if-out-of-view';
import WidgetForm from '../widget-form';
import type { Title } from '../..';

const { colors } = theme;
const mapWithKey = uncapFpIterator(map);

type EmbeddedWidgetProps = {
  buttonSpacing?: WidgetButtonSpacing;
  fluidWidth?: boolean;
  getData: GetData;
  isDestinationPage?: boolean;
  isWidgetActive: boolean;
  offer?: SectionOfferStrip;
  setIsWidgetActive: (value: boolean) => void;
  stepInputAppearance?: StepInputAppearance;
  submitButton?: SubmitButtonConfig;
  title: Title;
  topField?: ReactElement;
  wrapperPadding?: string;
  showFieldsRequiredLabel?: boolean;
};

const WidgetBody = ({
  buttonSpacing,
  fluidWidth,
  getData,
  isWidgetActive,
  offer,
  setIsWidgetActive,
  isDestinationPage,
  stepInputAppearance,
  submitButton,
  title,
  topField,
  wrapperPadding,
  showFieldsRequiredLabel = true,
}: EmbeddedWidgetProps) => {
  const { values } = useFormikContext<FormValues>();

  const {
    buttonSpacing: dataButtonSpacing,
    fields: data,
    fluidWidth: dataFluidWidth,
    wrapperPadding: dataWrapperPadding,
    stepInputAppearance: dataStepInputAppearance,
  } = getData(values);
  const resolvedButtonSpacing = buttonSpacing ?? dataButtonSpacing;
  const resolvedFluidWidth = fluidWidth ?? dataFluidWidth;
  const resolvedWrapperPadding = wrapperPadding ?? dataWrapperPadding;
  const resolvedStepInputAppearance = stepInputAppearance ?? dataStepInputAppearance;
  const [modalStates, setModalStates] = useState(map(() => false, data));
  const [currentModalIndex, setCurrentModalIndex] = useState<number | null>(null);
  const widgetWrapperRef = useRef<HTMLDivElement>(null);
  const widgetTopRef = useRef<HTMLDivElement>(null);
  const scrollToWidgetIfOutOfView = useScrollToRefIfOutOfView(widgetTopRef);

  const toggleModal = (modalPosition: number, value: boolean) => {
    setModalStates((modalState) =>
      mapWithKey((_state: number, index: number) => (index === modalPosition ? value : false), modalState),
    );
    setCurrentModalIndex(value ? modalPosition : null);
  };

  const closeAllModals = () => {
    setModalStates((modalState) => mapWithKey(() => false, modalState));
  };

  const setWidgetInactive = () => {
    scrollToWidgetIfOutOfView();
    setIsWidgetActive(false);
    closeAllModals();
  };

  const documentObj = browser.getDocument();

  useEffect(() => {
    if (isWidgetActive && device.isMobile()) {
      documentObj.body.style.overflow = 'hidden';
    }

    return () => {
      documentObj.body.style.overflow = 'auto';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWidgetActive]);

  useEffect(() => {
    if (isWidgetActive) {
      widgetWrapperRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isWidgetActive]);

  const TAB_KEY = 'Tab';
  // when tabbing back off the widget
  const handleCloseWidgetOnShiftTab = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === TAB_KEY && event.shiftKey) {
      setWidgetInactive();
    }
  };
  // when tabbing forward off the widget
  const handleCloseWidgetOnTab = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === TAB_KEY && !event.shiftKey) {
      setWidgetInactive();
    }
  };

  return (
    <>
      <Hidden lg df>
        <MobileToggleWrapper $displayMobileToggle={!isWidgetActive} $isDestinationPage={isDestinationPage}>
          {offer && (
            <OfferTongueWrapper $displayOffer={!isWidgetActive}>
              <OfferTongue offer={offer} />
            </OfferTongueWrapper>
          )}
          <MobileToggle $displayMobileToggle={!isWidgetActive} onClick={() => setIsWidgetActive(true)} isUnstyled>
            <HeadingWrapper>
              <StyledH2 looksLike="h5">{title.MOBILE}</StyledH2>
              <StyledP>{title.MOBILE_SUBTITLE}</StyledP>
            </HeadingWrapper>
            <MobileButton>
              <Icon name="arrow-right" fill={colors.white} size="large" />
            </MobileButton>
          </MobileToggle>
        </MobileToggleWrapper>
      </Hidden>

      <Hidden xs sm md>
        <Overlay $isShowing={isWidgetActive} onClick={setWidgetInactive} />
      </Hidden>
      <WidgetWrapper
        $displayWidget={isWidgetActive}
        $fluidWidth={resolvedFluidWidth}
        $isActive={isWidgetActive}
        $wrapperPadding={resolvedWrapperPadding}
        ref={widgetWrapperRef}
        $isDestinationPage={isDestinationPage}
      >
        <MenuWrapper ref={widgetTopRef}>
          {isWidgetActive && (
            <CloseButton aria-label="Close quote" onClick={setWidgetInactive} onKeyDown={handleCloseWidgetOnShiftTab}>
              <Icon name="cross-light" fill={colors.black} />
            </CloseButton>
          )}
          {offer && (
            <Hidden xs sm md>
              <OfferTongueWrapper $displayOffer={!isWidgetActive}>
                <OfferTongue offer={offer} />
              </OfferTongueWrapper>
            </Hidden>
          )}
          <TitleWrapper>
            <Hidden lg df>
              <Span looksLike="h4">{title.MOBILE}</Span>
            </Hidden>
            <Hidden xs sm md>
              <Span looksLike="h4">{title.DESKTOP}</Span>
            </Hidden>
            {topField}
          </TitleWrapper>
          {isWidgetActive && (
            <BackButton onClick={() => handleBackClick({ currentModalIndex, setWidgetInactive, toggleModal })}>
              <Icon name="app-chevron-left" fill={colors.black} />
            </BackButton>
          )}
        </MenuWrapper>

        {showFieldsRequiredLabel && (
          <LabelSpacer>
            <P>All fields marked with * are required</P>
          </LabelSpacer>
        )}
        <MobileScrollWrapper>
          <WidgetForm
            data={data}
            buttonSpacing={resolvedButtonSpacing}
            fluidWidth={resolvedFluidWidth}
            handleCloseWidgetOnTab={handleCloseWidgetOnTab}
            modalStates={modalStates}
            onModalClose={scrollToWidgetIfOutOfView}
            submitButton={submitButton}
            toggleModal={toggleModal}
            setIsWidgetActive={setIsWidgetActive}
            stepInputAppearance={resolvedStepInputAppearance}
            showFieldsRequiredLabel={showFieldsRequiredLabel}
          />
        </MobileScrollWrapper>
      </WidgetWrapper>
    </>
  );
};
export default WidgetBody;
